import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  listRecipes, Recipe, RecipeType, RecipeStatus,
  RECIPE_TYPES, RECIPE_STATUSES,
} from './api';
import './recipes.css';

const ADMIN_ROLES = ['Owner', 'Admin'];

const statusClass: Record<RecipeStatus, string> = {
  Active: 'rcp-tag-active',
  Draft: 'rcp-tag-draft',
  Archived: 'rcp-tag-archived',
};

const RecipeDashboard: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(searchParams.get('search') || '');

  const isAdmin = ADMIN_ROLES.includes(user?.role || '');
  const isManager = user?.role === 'Manager';
  const canEdit = isAdmin || isManager;
  const isStaff = user?.role === 'Employee';

  const filters = {
    search: searchParams.get('search') || undefined,
    status: (searchParams.get('status') as RecipeStatus) || (isStaff ? 'Active' : undefined),
    recipe_type: (searchParams.get('recipe_type') as RecipeType) || undefined,
    category: searchParams.get('category') || undefined,
  };

  const load = useCallback(() => {
    setLoading(true);
    listRecipes(sessionToken, filters)
      .then(r => setRecipes(r.recipes))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionToken, searchParams]);

  useEffect(() => { load(); }, [load]);

  const updateFilter = (key: string, value: string) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    setSearchParams(p);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateFilter('search', search);
  };

  // Derive unique categories for filter dropdown
  const categories = Array.from(new Set(recipes.map(r => r.category).filter(Boolean)));

  return (
    <div className="rcp-page">
      <div className="rcp-header">
        <h1>🍽️ Recipes & SOPs</h1>
        <div className="rcp-header-actions">
          {isAdmin && (
            <Link to="/recipes/import" className="rcp-btn">📥 Import Excel</Link>
          )}
          {canEdit && (
            <Link to="/recipes/new" className="rcp-btn rcp-btn-primary">+ New Recipe</Link>
          )}
        </div>
      </div>

      {error && <div className="rcp-error">{error}</div>}

      {/* Filters */}
      <div className="rcp-filters">
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.5rem', flex: 1 }}>
          <input
            className="rcp-input rcp-input-search"
            type="text"
            placeholder="Search recipes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button type="submit" className="rcp-btn">Search</button>
          {search && (
            <button type="button" className="rcp-btn" onClick={() => { setSearch(''); updateFilter('search', ''); }}>
              ✕
            </button>
          )}
        </form>

        {!isStaff && (
          <select
            className="rcp-select"
            value={searchParams.get('status') || ''}
            onChange={e => updateFilter('status', e.target.value)}
          >
            <option value="">All Statuses</option>
            {RECIPE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}

        <select
          className="rcp-select"
          value={searchParams.get('recipe_type') || ''}
          onChange={e => updateFilter('recipe_type', e.target.value)}
        >
          <option value="">All Types</option>
          {RECIPE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        {categories.length > 0 && (
          <select
            className="rcp-select"
            value={searchParams.get('category') || ''}
            onChange={e => updateFilter('category', e.target.value)}
          >
            <option value="">All Categories</option>
            {categories.map(c => <option key={c!} value={c!}>{c}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div className="rcp-loading">Loading recipes...</div>
      ) : recipes.length === 0 ? (
        <div className="rcp-empty">
          No recipes found.{canEdit && <> <Link to="/recipes/new">Create the first one</Link>.</>}
        </div>
      ) : (
        <div className="rcp-grid">
          {recipes.map(recipe => (
            <Link to={`/recipes/${recipe.id}`} key={recipe.id} className="rcp-card">
              <div className="rcp-card-title">{recipe.name}</div>
              <div className="rcp-card-meta">
                <span className={`rcp-tag rcp-tag-type`}>{recipe.recipe_type}</span>
                <span className={`rcp-tag ${statusClass[recipe.status]}`}>{recipe.status}</span>
                {recipe.category && (
                  <span className="rcp-tag rcp-tag-type" style={{ background: '#e0f2fe', color: '#0369a1' }}>
                    {recipe.category}
                  </span>
                )}
              </div>
              {(recipe.yield_quantity || recipe.prep_time_minutes) && (
                <div className="rcp-text-muted" style={{ fontSize: '0.8rem' }}>
                  {recipe.yield_quantity && (
                    <span>Yield: {recipe.yield_quantity} {recipe.yield_unit || ''}</span>
                  )}
                  {recipe.yield_quantity && recipe.prep_time_minutes && <span> · </span>}
                  {recipe.prep_time_minutes && <span>Prep: {recipe.prep_time_minutes} min</span>}
                </div>
              )}
              <div className="rcp-card-footer">
                <span>
                  {recipe.ingredient_count != null ? `${recipe.ingredient_count} ingredients` : ''}
                  {recipe.ingredient_count != null && recipe.step_count != null ? ' · ' : ''}
                  {recipe.step_count != null ? `${recipe.step_count} steps` : ''}
                </span>
                <span>v{recipe.version}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecipeDashboard;
